import { useDrawerStore } from '@nexelem/drawer';
import { useCustomToast } from '@nexelem/snackbar';
import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useInvalidateQuery, useCustomTranslation } from '../../../../hooks';
import { useAPI, GetBackItemsP1VariantsRequest, ItemTypeStatus } from '@nexelem/api';
import { useProductDetailsStore } from '../store';
import { useDetailsPath } from './useDetailsPath';

type HandleItemStatusProps = {
  readonly id?: number;
  readonly queryKey: string;
  readonly drawerName?: string;
};

export const useHandleItemStatus = ({ id, queryKey, drawerName }: HandleItemStatusProps) => {
  const api = useAPI();

  const onCloseDrawer = useDrawerStore((state) => state.onCloseDrawer);

  const displayToast = useCustomToast();

  const navigate = useNavigate();

  const detailsPath = useDetailsPath(id);

  const { tWrapper: tMessage } = useCustomTranslation('products.actionButtons');
  const { tWrapper: tError } = useCustomTranslation('components.toasts.commonMessage');

  const changeStatus = useProductDetailsStore((state) => state.changeStatus);

  const invalidateQueries = useInvalidateQuery(queryKey);

  const handleErrorToast = useCallback(() => {
    displayToast({
      toastType: 'error',
      message: tError('error'),
    });
  }, [displayToast, tError]);

  const handleSuccessToast = useCallback(
    (translationKey: 'activated' | 'deactivated') => {
      displayToast({
        toastType: 'success',
        message: tMessage('message', {
          state: tMessage(translationKey),
        }),
        firstButton: {
          label: tMessage('button'),
          onClick: () => navigate(detailsPath),
        },
      });
    },
    [detailsPath, displayToast, navigate, tMessage],
  );

  const onActivateProduct = useMutation({
    mutationKey: ['activate-product', id],
    mutationFn: () => api.getBackItemsReactivateP1({ p1: id } as GetBackItemsP1VariantsRequest),
    onSuccess: async () => {
      await invalidateQueries();
      if (drawerName) {
        onCloseDrawer(drawerName);
      }
      changeStatus(ItemTypeStatus.active);
      handleSuccessToast('activated');
    },
    onError: handleErrorToast,
  });

  const onDeactivateProduct = useMutation({
    mutationKey: ['deactivate-product', id],
    mutationFn: () => api.getBackItemsDeactivateP1({ p1: id } as GetBackItemsP1VariantsRequest),
    onSuccess: async () => {
      await invalidateQueries();
      if (drawerName) {
        onCloseDrawer(drawerName);
      }
      changeStatus(ItemTypeStatus.inactive);
      handleSuccessToast('deactivated');
    },
    onError: handleErrorToast,
  });

  return {
    onActivateProduct,
    onDeactivateProduct,
  };
};
